import {
  Navigate,
  Outlet,
  RouteObject,
  createBrowserRouter,
} from "react-router-dom";
import { BreadcrumbsRoute } from "use-react-router-breadcrumbs";

import CardRequiredGate from "~/components/autopto/cardDialog/CardRequiredGate";
import CardSetupReturn from "~/components/autopto/cardDialog/CardSetupReturn";
import GuestOnlyRoute from "~/components/guards/GuestOnlyRoute";
import OnboardingStepRedirect from "~/components/guards/OnboardingStepRedirect";
import OnboardingStepRoute from "~/components/guards/OnboardingStepRoute";
import SubscriptionRequiredRoute from "~/components/guards/SubscriptionRequiredRoute";
import OrderPaymentReturn from "~/components/services/orders/OrderPaymentReturn";
import FullscreenLoader from "~/components/ui/loaders/FullscreenLoader";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { BillingAccessProvider } from "~/contexts/BillingAccessContext";
import { FEATURE_FLAGS, FeatureGate } from "~/featureFlags";
import { HomeRoute } from "~/pages/HomeRoute";
import SiteWrapper from "~/pages/SiteWrapper";
import AutoptoOfferPage from "~/pages/autoptoOffer/AutoptoOfferPage";
import NewSitePage from "~/pages/dashboard/NewSitePage";
import LoginPage from "~/pages/login/LoginPage";
import OnboardingSurveyPage from "~/pages/onboarding/OnboardingSurveyPage";
import PlatformSetupPage from "~/pages/onboarding/PlatformSetupPage";
import ServicesSubscriptionsGate from "~/pages/services/ServicesSubscriptionsGate.tsx";
import { PATTERNS } from "~/router";
import { AppGate } from "~/router/AppGate";

import MainView from "../MainView";
import AuthContainer from "../components/containers/AuthContainer/AuthContainer";
import SiteSpeed from "../components/siteSpeed/SiteSpeed";
import ApiKeysPage from "../pages/apiKeys/ApiKeysPage";
import SubscriptionPage from "../pages/billing/SubscriptionPage";
import InvoicePage from "../pages/billing/invoice/InvoicesPage";
import PricingPage from "../pages/billing/pricing/PricingPage";
import SubscribePage from "../pages/billing/subscribe/SubscribePage";
import SiteSpeedDomainPage from "../pages/dashboard/siteSpeed/SiteSpeedDomainPage";
import SiteSpeedPage from "../pages/dashboard/siteSpeed/SiteSpeedPage";
import ActivateAccountPage from "../pages/login/ActivateAccountPage";
import CreateNewPasswordPage from "../pages/login/CreateNewPasswordPage";
import DemoLoginPage from "../pages/login/DemoLoginPage";
import ForgotPasswordPage from "../pages/login/ForgotPasswordPage";
import SignUpPage from "../pages/login/SignUpPage";
import UnlockPage from "../pages/login/UnlockPage";
import VerifyMfaPage from "../pages/login/VerifyMfaPage";
import SelectTeamPage from "../pages/selectTeam/SelectTeamPage";
import BrandLinksServicePage from "../pages/services/BrandLinksPage";
import CommunityLinksServicePage from "../pages/services/CommunityLinksPage";
import ConciergeInstallationPage from "../pages/services/ConciergeInstallationPage";
import LlmCitationsServicePage from "../pages/services/LlmCitationsServicePage";
import NicheLinksServicePage from "../pages/services/NicheLinksPage";
import OrdersPage from "../pages/services/OrdersPage";
import Settings from "../pages/settings/Settings";
import InvitationPage from "../pages/teamMembers/InvitationPage";
import TeamsMembersPage from "../pages/teamMembers/TeamMembersPage";
import { VISIBILITY_ENGINE_WIZARD_STEPS, siteRoutes } from "./site_routes";

const noSelectTeamRoutes = {
  path: PATTERNS.selectTeam,
  element: <SelectTeamPage />,
  handle: { auth: "protected", team: "optional" },
};

// Full-screen Autopto onboarding steps, outside MainView: no sidebar to leave
// the step through. The steps redirect on their own once completed.
const onboardingRoutes: RouteObject = {
  handle: { auth: "protected", team: "required" },
  children: [
    {
      path: PATTERNS.onboardingPlatform,
      element: (
        <OnboardingStepRoute step="platform">
          <PlatformSetupPage />
        </OnboardingStepRoute>
      ),
    },
    {
      path: PATTERNS.onboardingSurvey,
      element: (
        <OnboardingStepRoute step="survey">
          <OnboardingSurveyPage />
        </OnboardingStepRoute>
      ),
    },
  ],
};

const invitationRoute: RouteObject = {
  path: PATTERNS.invitation,
  element: <InvitationPage />,
  handle: { auth: "public" },
};

const verifyMfaRoute: RouteObject = {
  path: PATTERNS.verifyMfa,
  handle: { auth: "public" },
  element: (
    <AuthContainer>
      <VerifyMfaPage />
    </AuthContainer>
  ),
};

const unlockRoute: RouteObject = {
  path: PATTERNS.unlock,
  handle: { auth: "public" },
  element: (
    <AuthContainer>
      <UnlockPage />
    </AuthContainer>
  ),
};

const demoLoginRoute: RouteObject = {
  path: PATTERNS.demoLogin,
  handle: { auth: "public" },
  element: (
    <AuthContainer>
      <DemoLoginPage />
    </AuthContainer>
  ),
};

const activateAccountRoute: RouteObject = {
  path: PATTERNS.activateAccount,
  handle: { auth: "public" },
  element: (
    <AuthContainer>
      <ActivateAccountPage />
    </AuthContainer>
  ),
};

const authRoutes: RouteObject = {
  handle: { auth: "public" },
  element: (
    <GuestOnlyRoute>
      <AuthContainer>
        <Outlet />
      </AuthContainer>
    </GuestOnlyRoute>
  ),
  children: [
    {
      path: PATTERNS.login,
      element: <LoginPage />,
    },
    {
      path: PATTERNS.forgotPassword,
      element: <ForgotPasswordPage />,
    },
    {
      path: PATTERNS.createNewPassword,
      element: <CreateNewPasswordPage />,
    },
    {
      path: PATTERNS.signUp,
      element: <SignUpPage />,
    },
  ],
};

export const mainRoutes: BreadcrumbsRoute = {
  element: (
    <BillingAccessProvider>
      <MainView />
    </BillingAccessProvider>
  ),
  HydrateFallback: () => <FullscreenLoader />,
  handle: { auth: "protected", team: "required" },
  children: [
    {
      element: <SubscriptionRequiredRoute />,
      children: [
        {
          path: PATTERNS.home,
          element: (
            <FeatureGate feature={FEATURE_FLAGS.SITES} redirectToRoot>
              <HomeRoute />
            </FeatureGate>
          ),
        },
        {
          // Opened by the card dialog once the first card is in, never on
          // its own: it leaves for the dashboard without an offer.
          path: PATTERNS.autoptoOffer,
          element: <AutoptoOfferPage />,
        },
        {
          path: PATTERNS.newSiteWizard,
          breadcrumb: "Add Site",
          element: (
            <FeatureGate feature={FEATURE_FLAGS.SITES} redirectToRoot>
              <NewSitePage />
            </FeatureGate>
          ),
        },
        {
          path: PATTERNS.sites,
          element: (
            <FeatureGate feature={FEATURE_FLAGS.SITES} redirectToRoot>
              <SiteWrapper />
            </FeatureGate>
          ),
          children: [...siteRoutes],
        },
        {
          path: PATTERNS.apiKeys,
          element: (
            <FeatureGate feature={FEATURE_FLAGS.API_KEYS} redirectToRoot>
              <CardRequiredGate title={PAGE_TITLES.apiKeys}>
                <ApiKeysPage />
              </CardRequiredGate>
            </FeatureGate>
          ),
        },
        {
          path: PATTERNS.teamMembers,
          element: (
            <FeatureGate feature={FEATURE_FLAGS.TEAM_MEMBERS} redirectToRoot>
              <CardRequiredGate title={PAGE_TITLES.teamMembers}>
                <TeamsMembersPage />
              </CardRequiredGate>
            </FeatureGate>
          ),
        },
        {
          path: PATTERNS.visibilityEngine,
          children: [
            {
              path: "domains",
              breadcrumb: "AI Visibility Engine",
              element: (
                <FeatureGate feature={FEATURE_FLAGS.DOMAINS} redirectToRoot>
                  <SiteSpeedPage />
                </FeatureGate>
              ),
            },
            {
              path: "domains/:domainId",
              element: (
                <FeatureGate feature={FEATURE_FLAGS.DOMAINS} redirectToRoot>
                  <SiteSpeed />
                </FeatureGate>
              ),
            },
            ...VISIBILITY_ENGINE_WIZARD_STEPS.map(({ step, breadcrumb }) => ({
              path: `domains/:domainId/wizard/${step}`,
              breadcrumb,
              element: (
                <FeatureGate feature={FEATURE_FLAGS.DOMAINS} redirectToRoot>
                  <SiteSpeedDomainPage />
                </FeatureGate>
              ),
            })),
          ],
        },
        {
          path: PATTERNS.services,
          element: (
            <CardRequiredGate title={PAGE_TITLES.services}>
              <ServicesSubscriptionsGate />
            </CardRequiredGate>
          ),
          children: [
            {
              path: "orders",
              element: <OrdersPage />,
            },
            {
              path: "brand-links",
              element: (
                <FeatureGate
                  feature={FEATURE_FLAGS.SERVICES_BRAND_LINKS}
                  redirectToRoot
                >
                  <BrandLinksServicePage />
                </FeatureGate>
              ),
            },
            {
              path: "niche-links",
              element: (
                <FeatureGate
                  feature={FEATURE_FLAGS.SERVICES_NICHE_LINKS}
                  redirectToRoot
                >
                  <NicheLinksServicePage />
                </FeatureGate>
              ),
            },
            {
              path: "concierge-installation",
              element: (
                <FeatureGate
                  feature={FEATURE_FLAGS.SERVICES_CONCIERGE}
                  redirectToRoot
                >
                  <ConciergeInstallationPage />
                </FeatureGate>
              ),
            },
            {
              path: "llm-citations",
              element: (
                <FeatureGate
                  feature={FEATURE_FLAGS.SERVICES_LLM_CITATIONS}
                  redirectToRoot
                >
                  <LlmCitationsServicePage />
                </FeatureGate>
              ),
            },
            {
              path: "community-links",
              element: (
                <FeatureGate
                  feature={FEATURE_FLAGS.SERVICES_COMMUNITY_MENTIONS}
                  redirectToRoot
                >
                  <CommunityLinksServicePage />
                </FeatureGate>
              ),
            },
          ],
        },
        {
          path: PATTERNS.settings,
          element: (
            <FeatureGate feature={FEATURE_FLAGS.SETTINGS} redirectToRoot>
              <Settings />
            </FeatureGate>
          ),
          children: [
            {
              path: ":tabId",
              element: (
                <FeatureGate feature={FEATURE_FLAGS.SETTINGS} redirectToRoot>
                  <></>
                </FeatureGate>
              ),
            },
          ],
        },
      ],
    },
    {
      path: PATTERNS.billing,
      element: <OnboardingStepRedirect />,
      children: [
        {
          path: "subscription",
          element: (
            <FeatureGate feature={FEATURE_FLAGS.BILLING} redirectToRoot>
              <SubscriptionPage />
            </FeatureGate>
          ),
        },
        {
          // Legacy Stripe return_url landing. New checkout sessions use
          // redirect_on_completion: "never" and never redirect here, but
          // sessions created before that change live up to 24h and still
          // carry this return_url. Safe to delete once those expire.
          path: "checkout",
          element: (
            <FeatureGate feature={FEATURE_FLAGS.BILLING} redirectToRoot>
              <Navigate to={PATTERNS.billingSubscription} />
            </FeatureGate>
          ),
        },
        {
          path: "order",
          element: (
            <FeatureGate feature={FEATURE_FLAGS.BILLING} redirectToRoot>
              <OrderPaymentReturn />
            </FeatureGate>
          ),
        },
        {
          path: "setup",
          element: (
            <FeatureGate feature={FEATURE_FLAGS.BILLING} redirectToRoot>
              <CardSetupReturn />
            </FeatureGate>
          ),
        },
        {
          path: "subscription/edit",
          element: (
            <FeatureGate feature={FEATURE_FLAGS.BILLING} redirectToRoot>
              <PricingPage />
            </FeatureGate>
          ),
        },
        {
          path: "invoices",
          element: (
            <FeatureGate feature={FEATURE_FLAGS.BILLING} redirectToRoot>
              <InvoicePage />
            </FeatureGate>
          ),
        },
      ],
    },
    {
      path: PATTERNS.subscribe,
      element: (
        <OnboardingStepRedirect>
          <FeatureGate feature={FEATURE_FLAGS.BILLING} redirectToRoot>
            <SubscribePage />
          </FeatureGate>
        </OnboardingStepRedirect>
      ),
    },
  ],
};

const router = createBrowserRouter(
  [
    {
      element: <AppGate />,
      children: [
        invitationRoute,
        authRoutes,
        verifyMfaRoute,
        unlockRoute,
        demoLoginRoute,
        activateAccountRoute,
        noSelectTeamRoutes,
        onboardingRoutes,
        mainRoutes,
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

export default router;
